import { useEffect, useCallback, useState } from 'react';
import { User } from 'firebase/auth';
import { UserProfile } from '@/lib/firebase/users';
import { AUTOSAVE_DEBOUNCE_MS } from '@/config/writing-editor-config';
import { upsertWritingTask } from '@/lib/firebase/writing';
import { useEssayCoreState } from './use-essay-core-state';
import { useEssayDerivedState } from './use-essay-derived-state';
import { useEssayScoreManager } from './use-essay-score-manager';
import { useEssaySlashCommandManager } from './use-essay-slash-command-manager';
import { useEssayTaskBank } from './use-essay-task-bank';
import { useEssayAutoSave } from './use-essay-auto-save';
import {
  ReportEditorProps,
  WritingSaveData,
  ReportPlanItem,
  createStructuredReportData,
} from '@/types/writing';

interface ReportEditorStateProps extends ReportEditorProps {
  user: { uid: string } | null | undefined;
  userProfile: { role?: string } | null | undefined;
}

export function useReportEditorState({
  initialPrompt = '',
  initialIntroduction = '',
  initialOverview = '',
  initialDetailParagraphs = [
    { type: '', content: '' },
    { type: '', content: '' },
  ],
  initialReportPlan,
  initialEssayId = null,
  onSuggestionSuccess,
  suggestionClearTrigger,
  user,
  userProfile,
}: ReportEditorStateProps) {
  const isProUser =
    userProfile?.role === 'pro' || userProfile?.role === 'admin';

  const fullFirebaseUser = (user === undefined ? null : user) as User | null;
  const fullUserProfile = (
    userProfile === undefined ? null : userProfile
  ) as UserProfile | null;

  // Use core state but adapt for Task 1 semantics
  const coreState = useEssayCoreState({
    initialPrompt,
    initialIntroduction,
    initialBodyParagraphs: initialDetailParagraphs,
    initialConclusion: initialOverview, // Map overview to conclusion internally for now
    initialEssayId,
  });

  // Task 1 specific state
  const [overview, setOverview] = useState(initialOverview);

  // Sync overview with core state conclusion (temporary during transition)
  useEffect(() => {
    setOverview(coreState.conclusion);
  }, [coreState.conclusion]);

  const handleOverviewChange = useCallback(
    (value: string) => {
      setOverview(value);
      coreState.handleConclusionChange(value);
    },
    [coreState]
  );

  // Task 1 plan state (simpler than essay plan)
  const getInitialPlan = () => {
    if (initialReportPlan && initialReportPlan.length > 0) {
      return initialReportPlan;
    }
    return [
      { label: 'Overview', content: '' },
      { label: 'Detail Paragraph 1', content: '' },
      { label: 'Detail Paragraph 2', content: '' },
    ];
  };

  const [plan, setPlan] = useState<ReportPlanItem[]>(getInitialPlan());

  // State for accordion open/closed status with localStorage persistence
  const [isPlanOpen, setIsPlanOpen] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const storedState = localStorage.getItem('reportPlanOpen');
      return storedState ? JSON.parse(storedState) : true; // Default to open
    }
    return true; // Default to open for SSR or if window is not defined
  });

  // Effect to update localStorage when isPlanOpen changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('reportPlanOpen', JSON.stringify(isPlanOpen));
    }
  }, [isPlanOpen]);

  // Reset plan when essay ID changes
  useEffect(() => {
    setPlan(getInitialPlan());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEssayId, initialReportPlan]);

  const handlePlanContentChange = useCallback((idx: number, value: string) => {
    setPlan((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, content: value } : item))
    );
  }, []);

  const addPlanItem = useCallback(() => {
    setPlan((prev) => [
      ...prev,
      { label: `Detail Paragraph ${prev.length}`, content: '' },
    ]);
  }, []);

  const removePlanItem = useCallback((idx: number) => {
    setPlan((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const derivedState = useEssayDerivedState({
    introduction: coreState.introduction,
    bodyParagraphs: coreState.bodyParagraphs,
    conclusion: overview, // Use overview for word count calculations
  });

  const taskBankManager = useEssayTaskBank({
    initialPrompt: coreState.prompt,
    setPrompt: coreState.setPrompt,
  });

  const handleAddDetailParagraph = useCallback(() => {
    coreState.addBodyParagraph();
  }, [coreState]);

  const handleRemoveDetailParagraph = useCallback(
    (idx: number) => {
      coreState.removeBodyParagraph(idx);
    },
    [coreState]
  );

  const autoSaveManager = useEssayAutoSave({
    writingData: {
      taskType: 'task1',
      prompt: coreState.prompt,
      introduction: coreState.introduction,
      bodyParagraphs: coreState.bodyParagraphs,
      conclusion: overview, // Save overview in conclusion field for backward compatibility
      overview: overview, // Also save in proper overview field
      reportPlan: plan, // Include report plan in auto-save
    } as WritingSaveData,
    user: fullFirebaseUser,
    initialEssayId,
    handleSave: async (data: WritingSaveData, id?: string | null) => {
      if (!fullFirebaseUser) throw new Error('User must be logged in to save.');
      return upsertWritingTask(fullFirebaseUser.uid, data, id);
    },
    debounceMs: AUTOSAVE_DEBOUNCE_MS,
  });

  const handleSuggestionSuccess = onSuggestionSuccess || (() => {});

  const scoreManager = useEssayScoreManager({
    userId: fullFirebaseUser?.uid,
    currentEssayId: autoSaveManager.currentEssayId,
    userProfile: fullUserProfile,
    isProUser,
    onScoreSuccess: handleSuggestionSuccess,
  });

  useEffect(() => {
    if (suggestionClearTrigger && suggestionClearTrigger > 0) {
      scoreManager.clearScore();
    }
  }, [suggestionClearTrigger, scoreManager]);

  const slashCommandManager = useEssaySlashCommandManager({
    introduction: coreState.introduction,
    bodyParagraphs: coreState.bodyParagraphs,
    conclusion: overview, // Use overview for slash commands
    essayPlan: plan,
    prompt: coreState.prompt,
    user: fullFirebaseUser,
    selectedTask: taskBankManager.taskBank.selectedTask,
    taskType: 'task1',
  });

  return {
    prompt: coreState.prompt,
    setPrompt: coreState.setPrompt,
    introduction: coreState.introduction,
    bodyParagraphs: coreState.bodyParagraphs,
    overview, // Expose overview instead of conclusion
    handleIntroductionChange: coreState.handleIntroductionChange,
    handleOverviewChange, // Proper overview handler
    handlePromptChange: coreState.handlePromptChange,
    handleBodyParagraphChange: coreState.handleBodyParagraphChange,
    addBodyParagraph: handleAddDetailParagraph,
    removeBodyParagraph: handleRemoveDetailParagraph,

    plan,
    setPlan,
    handlePlanContentChange,
    addPlanItem,
    removePlanItem,
    isPlanOpen,
    setIsPlanOpen,

    isSlashMenuOpen: slashCommandManager.isSlashMenuOpen,
    slashMenuTargetElement: slashCommandManager.slashMenuTargetElement,
    slashMenuFilteredCommands: slashCommandManager.slashMenuFilteredCommands,
    slashMenuSelectedIndex: slashCommandManager.slashMenuSelectedIndex,
    setSlashMenuSelectedIndex: slashCommandManager.setSlashMenuSelectedIndex,
    handleSlashClose: slashCommandManager.handleSlashClose,
    handleSlashCommandTextareaKeyDown:
      slashCommandManager.handleSlashCommandTextareaKeyDown,
    handleSlashTrigger: slashCommandManager.handleSlashTrigger,
    handleSlashFilterUpdate: slashCommandManager.handleSlashFilterUpdate,
    executeSlashCommand: slashCommandManager.executeSlashCommand,
    isCommandResultPopoverOpen: slashCommandManager.isCommandResultPopoverOpen,
    isCommandLoading: slashCommandManager.isCommandLoading,
    commandResult: slashCommandManager.commandResult,
    commandError: slashCommandManager.commandError,
    commandResultTargetElement: slashCommandManager.commandResultTargetElement,
    closeCommandResultPopover: slashCommandManager.closeCommandResultPopover,
    activeCommandId: slashCommandManager.activeCommandId,
    slashMenuTriggerContext: slashCommandManager.slashMenuTriggerContext,

    taskBank: taskBankManager.taskBank,

    saveStatus: autoSaveManager.saveStatus,
    currentEssayId: autoSaveManager.currentEssayId,
    user: fullFirebaseUser,
    isProUser,
    score: scoreManager.score,
    scoreError: scoreManager.scoreError,
    isScoring: scoreManager.isScoring,
    showMinimalChangeDialog: scoreManager.showMinimalChangeDialog,
    setShowMinimalChangeDialog: scoreManager.setShowMinimalChangeDialog,
    fetchNewScore: scoreManager.fetchScore,
    clearScore: scoreManager.clearScore,
    hasHistory: scoreManager.hasHistory,
    isCheckingHistory: scoreManager.isCheckingHistory,
    totalWordCount: derivedState.totalWordCount,
    currentContent: derivedState.currentContent,
    canScore: scoreManager.checkScoringRequirements,
    scoringRequirementsList: scoreManager.checkScoringRequirements(
      createStructuredReportData(
        coreState.introduction,
        coreState.bodyParagraphs,
        overview
      ),
      'task1'
    ),
  };
}
